import { useEffect, useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { useLocation, useNavigate } from 'react-router-dom';

import SiteNavbar from '../components/SiteNavbar';
import OtpInput from '../components/OtpInput';
import { supabase, setAccessToken } from '../lib/supabaseClient';
import { ensureFrontendSessionId } from '../lib/session';

const RESEND_OTP_AFTER_MS = 30_000; // 30 seconds

function useQuery() {
  const { search } = useLocation();
  return new URLSearchParams(search);
}

export default function VerifyOtpPage() {
  const navigate = useNavigate();
  const query = useQuery();

  const email = query.get('email') ?? '';
  const context = query.get('context') ?? 'login'; // login | signup | waitlist

  const [errorMsg, setErrorMsg] = useState<string>('');
  const [resendDisabled, setResendDisabled] = useState<boolean>(true);

  // Enable the “Resend” link after 30 s
  useEffect(() => {
    setResendDisabled(true);
    const id = setTimeout(() => setResendDisabled(false), RESEND_OTP_AFTER_MS);
    return () => clearTimeout(id);
  }, [email]);

  const verify = useCallback(
    async (code: string) => {
      setErrorMsg('');

      const endpoint = context === 'waitlist' ? 'waitlist_verify_otp' : 'auth_verify_otp';

      const sessionId = await ensureFrontendSessionId();

      const { data, error } = await supabase.functions.invoke(endpoint, {
        body: {
          email,
          code,
          sessionId,
        },
      });

      if (error) {
        setErrorMsg(error.message);

        Sentry.captureMessage('otp_verification_failed', {
          level: 'warning',
          extra: { email, context, error: error.message },
        });
        return false;
      }

      if (!data?.verified) {
        setErrorMsg('Incorrect or expired code');
        return false;
      }

      // Store JWT issued by auth_verify_otp (HS256, D1-backed)
      if (data.access_token) {
        setAccessToken(data.access_token as string);
      }

      // Decide where to go next -------------------------
      if (data.waitlisted) {
        Sentry.addBreadcrumb({ category: 'auth', message: 'waitlist_verified', level: 'info', data: { email } });
        navigate('/waitlisted');
        return true;
      }

      if (data.role === 'student') {
        navigate('/student');
      } else {
        // default → educator dashboard
        navigate('/app');
      }

      Sentry.addBreadcrumb({ category: 'auth', message: 'login_verified', level: 'info', data: { email, role: data.role } });

      return true;
    },
    [email, context, navigate],
  );

  const resend = async () => {
    if (resendDisabled) return;
    setResendDisabled(true);

    const endpoint = context === 'waitlist' ? 'waitlist_request_otp' : 'auth_request_otp';
    const sessionId = await ensureFrontendSessionId();
    await supabase.functions.invoke(endpoint, {
      body: {
        email,
        sessionId,
      },
    });

    // restart timer
    const id = window.setTimeout(() => setResendDisabled(false), RESEND_OTP_AFTER_MS);
    return () => window.clearTimeout(id);
  };

  return (
    <div className="flex min-h-screen flex-col bg-white dark:bg-gray-900">
      <SiteNavbar />

      <main className="flex flex-1 items-center justify-center px-4 py-16">
        <div className="w-full max-w-md rounded-lg bg-gray-50 p-8 shadow-md dark:bg-gray-800">
          <h1 className="mb-4 text-center text-2xl font-bold text-gray-900 dark:text-gray-50">Enter the 6-digit code</h1>

          <p className="mb-6 text-center text-sm text-gray-700 dark:text-gray-300">
            We’ve sent a code to <span className="font-medium">{email}</span>
          </p>

          <OtpInput length={6} onComplete={verify} className="mx-auto max-w-xs" />

          {errorMsg && <p className="mt-4 text-center text-xs text-red-600">{errorMsg}</p>}

          <button
            type="button"
            onClick={resend}
            disabled={resendDisabled}
            className="mt-6 block w-full text-center text-sm font-medium text-primary disabled:opacity-50"
          >
            Resend code
          </button>
        </div>
      </main>
    </div>
  );
}
